import FieldStrategy from '../FieldStrategy';
import { generateFieldName } from '@/views/fusion/investment/intelligence-create/tools';
import { generateDynamicFormModel, generateHtmlContent } from '@/views/fusion/investment/intelligence-create/tools/form';
import { clone, deepMerge } from '@/utils';
import { isNotNullAndUnDef } from '@/utils/is';
import { checkboxFilterNone, transformSelectedTimeToTimeSeries } from '@/tools/form';
import {
  CAMPAIGN_MODULE_KEY,
  CREATIVE_MODULE_KEY,
  CREATIVE_COMPONENTS_FORM_KEY,
  TARGETING_MODULE_KEY,
  MATERIAL_MODULE_KEY,
  TITLE_MODULE_KEY,
  SITE_MODULE_KEY,
} from '@/views/fusion/investment/intelligence-create/strategy/constants';
import { OPTIONS_TYPE, CHANNEL_INTERFACE_TYPE, TIME_PERIOD_LENGTH } from '@/enums';
import calculator from '@/utils/calculator';

const brand_name = {
  label: '品牌名称',
  type: 'input',
  props: {
    placeholder: '请输入品牌名称',
    maxlength: 18,
    showWordLimit: true,
  },
  value: '',
  rules: [],
};

const brand_logo = {
  label: '品牌LOGO',
  type: 'image',
  value: '',
  isPreview: false,
  limit: 1,
  is_open: false,
  element_type: null,
  temp_material: null,
  material_restriction: [],
  rules: [],
};

const button_text = {
  label: '按钮文案',
  type: 'select',
  options: [],
  value: null,
  default_value: '立即下载',
  rules: [],
};

const video_background_image = {
  label: '视频结束页背景图',
  type: 'image',
  value: '',
  isPreview: false,
  limit: 1,
  is_open: false,
  element_type: null,
  temp_material: null,
  material_restriction: [],
  rules: [],
};

// 【创意组件】表单配置
const creative_components_config = {
  brand_name,
  brand_logo,
  button_text,
  video_background_image,
};

/**
 * OPPO策略类
 */
export default class OppoFieldStrategy extends FieldStrategy {
  /**
   * 获取差异化模块字段
   * @returns {Object}
   */
  getDifferentiatedFields() {
    return {
      campaign_config: {
        config: {
          delivery_form: {
            config: {
              delivery_platform_type: {
                value: '2',
                backup_value: '2',
              },
              delivery_content_id: {
                type: '',
                is_single_advertiser_assign: true,
                is_open: false,
                value: {
                  type_selection: [
                    { label: 'Android应用', value: '2' },
                  ],
                  result_map: {},
                },
                isPreview: false,
                rules: {
                  required: true,
                  trigger: ['change'],
                  validator: (_, value, callback) => {
                    if (Object.keys(value.result_map).length) {
                      callback();
                    } else {
                      callback(new Error('请选择投放内容'));
                    }
                  },
                },
              },
              instant_app_url: {
                label: '快应用直达链接',
                type: 'input',
                hidden: true,
                prepend: {
                  content: 'hap://game/',
                },
                append: {
                  content: '/?_FORCE_UPDATE_=1',
                },
                props: {
                  placeholder: '请输入快应用包名',
                },
                value: '',
                rules: { required: true, message: '请输入快应用包名', trigger: ['change'] },
              },
            },
          },
          site_form: {
            config: {
              site_set_type: {
                labelTips: generateHtmlContent({
                  content: [
                    '自有流量：展示优选、信息流',
                    '联盟流量：联盟通投、Banner、插屏、开屏、原生、激励视频',
                  ],
                }),
                props: {
                  disabled: true,
                },
                value: 2,
              },
              site_sets: {
                hidden: false,
                props: {
                  max: 1,
                },
                value: ['13'],
                backup_value: ['13'],
                rules: { required: true, message: '请选择广告版位', trigger: ['change'] },
              },
              link_desk_flag: {
                label: '快应用加桌',
                type: 'radio-group',
                hidden: true,
                isButton: true,
                options: [
                  { label: '开启', value: '1' },
                  { label: '关闭', value: '0' },
                ],
                value: '1',
              },
            },
          },
          budget_and_bid_form: {
            config: {
              price_type: {
                options_map: {
                  '13': [
                    { label: 'CPD', value: '1' },
                    { label: 'oCPD', value: '5' },
                  ],
                  default: [
                    { label: 'CPC', value: '2' },
                    { label: 'oCPC', value: '5' },
                  ],
                },
                value: '1',
              },
              price: {
                hidden: false,
              },
              price_strategy: {
                hidden: true,
                value: '0',
              },
              convert_id: {
                hidden: true,
              },
              fusion__optimize_goal: {
                label: '优化目标',
                type: 'select',
                hidden: true,
                props: {
                  clearable: false,
                },
                options: [],
                value: '',
                backup_value: '',
                rules: { required: true, message: '请选择优化目标', trigger: ['change'] },
              },
              deep_convert_type: {
                hidden: true,
              },
              default_second_stage: {
                label: '广告免一阶',
                type: 'radio-group',
                hidden: true,
                isButton: true,
                props: {
                  disabled: false,
                },
                options: [
                  { label: '开启', value: '1' },
                  { label: '关闭', value: '0' },
                ],
                value: '1',
              },
              ocpx_optimize_switch: {
                label: '自动优化',
                type: 'radio-group',
                hidden: true,
                isButton: true,
                options: [
                  { label: '开启', value: '1' },
                  { label: '关闭', value: '0' },
                ],
                value: '0',
              },
              ocpx_optimize_type: {
                label: '自动优化目标',
                type: 'select',
                hidden: true,
                options: [],
                value: '',
                rules: { required: true, message: '请选择自动优化目标', trigger: ['change'] },
              },
              ocpc_price: {
                label: '优化目标出价',
                type: 'input-number',
                hidden: true,
                props: {
                  step: 1,
                  precision: 2,
                  min: 0.01,
                  max: 999999999.00,
                },
                formItemTips: {
                  props: {
                    class: 'inline-block',
                    style: {
                      marginLeft: '10px',
                    },
                  },
                  content: '元',
                },
                value: undefined,
                rules: { required: true, message: '请输入优化目标出价', trigger: ['change'] },
              },
              target_roi: {
                label: '深度ROI系数出价',
                type: 'input-number',
                hidden: true,
                props: {
                  step: 1,
                  precision: 3,
                  min: 0.001,
                  max: 1000,
                },
                value: undefined,
                rules: { required: true, message: '请输入深度ROI系数出价', trigger: ['change'] },
              },
            },
          },
        },
      },
      creative_config: {
        config: {
          content_form: {
            config: {
              delivery_mode: {
                label: '投放模式',
                type: 'radio-group',
                isButton: true,
                props: {
                  disabled: true,
                },
                options: [
                  { label: '组件化创意', value: OPTIONS_TYPE.UNLIMITED },
                  { label: '自定义创意', value: OPTIONS_TYPE.CUSTOM },
                ],
                value: OPTIONS_TYPE.CUSTOM,
              },
              creative_template_id: {
                label: '创意形式',
                type: 'radio-group',
                isBox: true,
                options: [],
                value: null,
                backup_value: null,
              },
            },
          },
        },
      },
      targeting_config: {
        config: {
          targeting_form: {
            config: {
              // 自定义人群
              audience: {
                children: {
                  include_audiences: {
                    props: {
                      maxCount: 1,
                    },
                  },
                  exclude_audiences: {
                    props: {
                      maxCount: 1,
                    },
                  },
                },
              },
              // 年龄
              ages: {
                value: {},
                children: {
                  ages_selection: {
                    label: '年龄',
                    type: 'radio-group',
                    isButton: true,
                    options: [
                      { label: '不限', value: OPTIONS_TYPE.UNLIMITED },
                      { label: '指定年龄段', value: OPTIONS_TYPE.CUSTOM },
                    ],
                    value: OPTIONS_TYPE.UNLIMITED,
                  },
                  ages: {
                    label: ' ',
                    type: 'checkbox-group',
                    hidden: true,
                    isBox: true,
                    options: [],
                    value: [],
                    rules: { required: true, message: '请选择年龄段', trigger: ['change'] },
                  },
                },
              },
              // 网络
              network_types: {
                label: '网络',
                type: 'checkbox-group',
                isButton: true,
                options: [],
                value: [OPTIONS_TYPE.UNLIMITED],
              },
              // 机型价格
              device_prices: {
                label: '机型价格',
                type: 'checkbox-group',
                isButton: true,
                options: [],
                value: [OPTIONS_TYPE.UNLIMITED],
              },
              // 机型
              devices: {
                value: {},
                children: {
                  device_selection: {
                    label: '机型',
                    type: 'radio-group',
                    isButton: true,
                    options: [
                      { label: '不限', value: OPTIONS_TYPE.UNLIMITED },
                      { label: '自定义', value: OPTIONS_TYPE.CUSTOM },
                    ],
                    value: OPTIONS_TYPE.UNLIMITED,
                  },
                  devices: {
                    label: ' ',
                    type: 'tree-shuttle',
                    name: '机型',
                    props: {
                      nodeKey: 'value',
                      maxCount: 100,
                    },
                    hidden: true,
                    options: [],
                    value: [],
                    rules: {
                      required: true,
                      trigger: ['blur'],
                      validator: (_, value, callback) => {
                        if (value.length) {
                          callback();
                        } else {
                          callback(new Error('请选择机型'));
                        }
                      },
                    },
                  },
                },
              },
              // APP安装
              app_install: {
                value: {},
                children: {
                  app_install_selection: {
                    label: 'APP安装',
                    type: 'radio-group',
                    isButton: true,
                    options: [
                      { label: '不限', value: OPTIONS_TYPE.UNLIMITED },
                      { label: '已安装', value: 'install_app_list' },
                      { label: '当前未安装', value: 'not_install_app_list' },
                      { label: '历史未安装', value: 'never_install_app_list' },
                      { label: '前一天安装', value: 'yesterday_installed_app' },
                      { label: '已安装未激活', value: 'installed_never_active_app_list' },
                    ],
                    value: OPTIONS_TYPE.UNLIMITED,
                  },
                  app_install: {
                    label: ' ',
                    type: 'tree-shuttle',
                    name: 'APP安装',
                    props: {
                      nodeKey: 'value',
                      maxCount: 30,
                    },
                    hidden: true,
                    options: [],
                    value: [],
                    rules: {
                      required: true,
                      trigger: ['blur'],
                      validator: (_, value, callback) => {
                        if (value.length) {
                          callback();
                        } else {
                          callback(new Error('请选择APP安装'));
                        }
                      },
                    },
                  },
                  app_uninstall_days: {
                    label: '未安装天数',
                    type: 'select',
                    hidden: true,
                    options: [],
                    value: '180',
                    rules: { required: true, message: '请选择未安装天数', trigger: ['change'] },
                  },
                },
              },
              // 推广应用安装
              promote_app_installed: {
                value: {},
                children: {
                  promote_app_installed_selection: {
                    label: '推广应用安装',
                    type: 'radio-group',
                    isButton: true,
                    options: [
                      { label: '不限', value: OPTIONS_TYPE.UNLIMITED },
                      { label: '已安装该应用', value: 'promote_app_installed' },
                      { label: '未安装该应用', value: 'promote_app_not_installed' },
                    ],
                    value: OPTIONS_TYPE.UNLIMITED,
                  },
                  promote_app_not_installed: {
                    label: '最近卸载时间',
                    type: 'select',
                    hidden: true,
                    options: [],
                    value: '0',
                    rules: { required: true, message: '请选择最近卸载时间', trigger: ['change'] },
                  },
                },
              },
              // 用户活跃
              app_active: {
                value: {},
                children: {
                  app_active_selection: {
                    label: '用户活跃',
                    type: 'radio-group',
                    isButton: true,
                    options: [
                      { label: '不限', value: OPTIONS_TYPE.UNLIMITED },
                      { label: '应用活跃', value: 'app_active' },
                      { label: '应用非活跃', value: 'app_not_active' },
                    ],
                    value: OPTIONS_TYPE.UNLIMITED,
                  },
                  app_active: {
                    label: ' ',
                    type: 'tree-shuttle',
                    name: '用户活跃',
                    props: {
                      nodeKey: 'value',
                      maxCount: 100,
                    },
                    hidden: true,
                    options: [],
                    value: [],
                    rules: {
                      required: true,
                      trigger: ['blur'],
                      validator: (_, value, callback) => {
                        if (value.length) {
                          callback();
                        } else {
                          callback(new Error('请选择用户活跃'));
                        }
                      },
                    },
                  },
                  app_active_days: {
                    label: '用户活跃天数',
                    type: 'input-number',
                    hidden: true,
                    props: {
                      precision: 0,
                      step: 1,
                      min: 1,
                      max: 180,
                    },
                    value: 1,
                    rules: { required: true, message: '请输入用户活跃天数', trigger: ['change'] },
                  },
                },
              },
              // 用户兴趣行为
              interest_tags_v2: {
                value: {},
                children: {
                  interest_tags_v2_selection: {
                    label: '用户兴趣行为',
                    type: 'radio-group',
                    isButton: true,
                    options: [
                      { label: '不限', value: OPTIONS_TYPE.UNLIMITED },
                      { label: '自定义', value: OPTIONS_TYPE.CUSTOM },
                    ],
                    value: OPTIONS_TYPE.UNLIMITED,
                  },
                  interest_tags_v2: {
                    label: ' ',
                    type: 'tree-shuttle',
                    name: '用户兴趣行为',
                    props: {
                      nodeKey: 'value',
                    },
                    hidden: true,
                    options: [],
                    value: [],
                    rules: {
                      required: true,
                      trigger: ['blur'],
                      validator: (_, value, callback) => {
                        if (value.length) {
                          callback();
                        } else {
                          callback(new Error('请选择用户兴趣行为'));
                        }
                      },
                    },
                  },
                },
              },
              // 快应用桌面状态
              instant_install_list: {
                value: {},
                children: {
                  instant_install_list_selection: {
                    label: '快应用桌面状态',
                    type: 'radio-group',
                    isButton: true,
                    options: [
                      { label: '不限', value: OPTIONS_TYPE.UNLIMITED },
                      { label: '已添加到桌面', value: 'instant_install_list' },
                      { label: '未添加到桌面', value: 'instant_not_install_list' },
                    ],
                    value: OPTIONS_TYPE.UNLIMITED,
                  },
                  instant_install_list: {
                    label: ' ',
                    type: 'tree-shuttle',
                    name: '快应用桌面状态',
                    props: {
                      nodeKey: 'value',
                      maxCount: 500,
                    },
                    hidden: true,
                    options: [],
                    value: [],
                    rules: {
                      required: true,
                      trigger: ['blur'],
                      validator: (_, value, callback) => {
                        if (value.length) {
                          callback();
                        } else {
                          callback(new Error('请选择快应用桌面状态'));
                        }
                      },
                    },
                  },
                },
              },
              // 预装APP
              pre_install_app_list: {
                value: {},
                children: {
                  pre_install_app_list_selection: {
                    label: '预装APP',
                    type: 'radio-group',
                    isButton: true,
                    options: [
                      { label: '不限', value: OPTIONS_TYPE.UNLIMITED },
                      { label: '预装', value: 'pre_install_app_list' },
                      { label: '非预装', value: 'un_pre_install_app_list' },
                    ],
                    value: OPTIONS_TYPE.UNLIMITED,
                  },
                  pre_install_app_list: {
                    label: ' ',
                    type: 'tree-shuttle',
                    name: '预装APP',
                    props: {
                      nodeKey: 'value',
                      maxCount: 30,
                    },
                    hidden: true,
                    options: [],
                    value: [],
                    rules: {
                      required: true,
                      trigger: ['blur'],
                      validator: (_, value, callback) => {
                        if (value.length) {
                          callback();
                        } else {
                          callback(new Error('请选择预装APP'));
                        }
                      },
                    },
                  },
                },
              },
              // 流量定向
              union_app_id: {
                value: {},
                children: {
                  union_app_id_selection: {
                    label: '流量定向',
                    type: 'radio-group',
                    isButton: true,
                    options: [
                      { label: '不限', value: OPTIONS_TYPE.UNLIMITED },
                      { label: '定向应用', value: 'union_app_id' },
                      { label: '屏蔽应用', value: 'union_app_id_re' },
                    ],
                    value: OPTIONS_TYPE.UNLIMITED,
                  },
                  union_app_id: {
                    label: ' ',
                    type: 'tree-shuttle',
                    name: '流量定向',
                    props: {
                      nodeKey: 'value',
                      maxCount: 500,
                    },
                    hidden: true,
                    options: [],
                    value: [],
                    rules: {
                      required: true,
                      trigger: ['blur'],
                      validator: (_, value, callback) => {
                        if (value.length) {
                          callback();
                        } else {
                          callback(new Error('请选择流量定向'));
                        }
                      },
                    },
                  },
                },
              },
              // 屏蔽已转化用户
              filter_converted_ad: {
                value: {},
                children: {
                  filter_converted_ad: {
                    label: '屏蔽已转化用户',
                    type: 'radio-group',
                    isButton: true,
                    options: [
                      { label: '不限', value: OPTIONS_TYPE.UNLIMITED },
                      { label: '广告组', value: 2 },
                      { label: '广告计划', value: 3 },
                      { label: '广告主', value: 4 },
                      { label: '公司主体', value: 5 },
                      { label: 'APP', value: 6 },
                    ],
                    value: OPTIONS_TYPE.UNLIMITED,
                  },
                  filter_converted_ad_days: {
                    label: '过滤时间',
                    type: 'radio-group',
                    hidden: true,
                    isButton: true,
                    options: [],
                    value: '1',
                    rules: { required: true, message: '请选择过滤时间', trigger: ['change'] },
                  },
                },
              },
            },
          },
        },
      },
      material_config: {
        config: {
          material_form: {
            config: {
              material: {
                props: {
                  type: 'image',
                  tempMaterial: null,
                },
                materialRestriction: [],
                material_type: null,
              },
            },
          },
        },
      },
    };
  }

  /**
   * 添加事件
   * @param {Object} config 模块配置
   * @param {Object} controller 控制器
   */
  addEvent(config, controller) {
    this.config = config;
    super.addEvent(config);
    const {
      campaign_config: {
        config: {
          delivery_form,
          site_form,
          budget_and_bid_form,
        },
      },
    } = config;

    const { delivery_platform_type, delivery_content_id, instant_app_url } = delivery_form.config;
    const { site_sets, link_desk_flag } = site_form.config;
    const {
      daily_budget_type,
      price_type,
      price,
      price_strategy,
      fusion__optimize_goal,
      deep_convert_type,
      deep_convert_price,
      default_second_stage,
      ocpx_optimize_switch,
      ocpx_optimize_type,
      ocpc_price,
      target_roi,
    } = budget_and_bid_form.config;

    // 投放平台
    delivery_platform_type.on = {
      change: async() => {
        const is_android = delivery_platform_type.value === '2';
        instant_app_url.hidden = is_android;
        if (delivery_platform_type.value !== delivery_platform_type.backup_value) {
          delivery_platform_type.backup_value = delivery_platform_type.value;

          delivery_content_id.value.result_map = {};
          delivery_content_id.value.type_selection.splice(0);
          delivery_content_id.value.type_selection.push(...[
            ...(delivery_platform_type.value === '2' ? [{ label: 'Android应用', value: '2' }] : []),
            ...(delivery_platform_type.value === '3' ? [{ label: '快应用', value: '3' }] : []),
          ]);

          // 获取版位
          site_sets.options = await controller.getSiteSet({
            oppo_params: {
              filtering: {
                delivery_platform_type: delivery_platform_type.value,
              },
            },
          });
          const is_match_site_sets = !site_sets.value.length ? false : site_sets.options.some(({ value }) => site_sets.value[0] === value);
          if (!is_match_site_sets) {
            site_sets.value = [site_sets.options[0].value];
            site_sets.backup_value = [site_sets.options[0].value];
          }

          // 获取优化目标
          fusion__optimize_goal.options = await controller.getConvertGoalList({
            oppo_params: {
              filtering: {
                ...(site_sets.value.length && { site_set_id: site_sets.value[0] }),
                delivery_platform_type: delivery_platform_type.value,
              },
            },
          });
          const is_match_optimize_goal = fusion__optimize_goal.options.some(({ value }) => fusion__optimize_goal.value === value);
          if (!is_match_optimize_goal) {
            fusion__optimize_goal.value = '';
            fusion__optimize_goal.backup_value = '';
            deep_convert_type.value = '';
            deep_convert_type.options = [];
          }

          // 获取深度优化目标
          if (fusion__optimize_goal.value) {
            deep_convert_type.options = await controller.getDeepConvertGoalList({
              oppo_params: {
                filtering: {
                  ...(site_sets.value.length && { site_set_id: site_sets.value[0] }),
                  delivery_platform_type: delivery_platform_type.value,
                  convert_goal: fusion__optimize_goal.value,
                },
              },
            });
            const is_match_deep_convert_type = deep_convert_type.options.some(({ value }) => deep_convert_type.value === value);
            !is_match_deep_convert_type && (deep_convert_type.value = deep_convert_type.options.length ? deep_convert_type.options[0].value : '');
          }

          site_sets.on.change();
        }
      },
    };

    // 投放内容
    delivery_content_id.on = {
      change: () => {},
    };

    // 广告版位
    site_sets.on = {
      change: async() => {
        if (!site_sets.value.length) {
          return;
        }
        if (site_sets.value[0] !== site_sets.backup_value[0]) {
          site_sets.backup_value = site_sets.value;

          // 获取优化目标
          fusion__optimize_goal.options = await controller.getConvertGoalList({
            oppo_params: {
              filtering: {
                ...(site_sets.value.length && { site_set_id: site_sets.value[0] }),
                delivery_platform_type: delivery_platform_type.value,
              },
            },
          });
          const is_match_optimize_goal = fusion__optimize_goal.options.some(({ value }) => fusion__optimize_goal.value === value);
          if (!is_match_optimize_goal) {
            fusion__optimize_goal.value = '';
            fusion__optimize_goal.backup_value = '';
            deep_convert_type.value = '';
            deep_convert_type.options = [];
          }

          // 获取深度优化目标
          if (fusion__optimize_goal.value) {
            deep_convert_type.options = await controller.getDeepConvertGoalList({
              oppo_params: {
                filtering: {
                  ...(site_sets.value.length && { site_set_id: site_sets.value[0] }),
                  delivery_platform_type: delivery_platform_type.value,
                  convert_goal: fusion__optimize_goal.value,
                },
              },
            });
            const is_match_deep_convert_type = deep_convert_type.options.some(({ value }) => deep_convert_type.value === value);
            !is_match_deep_convert_type && (deep_convert_type.value = deep_convert_type.options.length ? deep_convert_type.options[0].value : '');
          }
        }
        const is_13 = site_sets.value[0] === '13';
        const is_app = delivery_platform_type.value === '3';
        link_desk_flag.hidden = !(['6'].includes(site_sets.value[0]) && is_app);
        price_type.options = price_type.options_map[is_13 ? '13' : 'default'];
        const is_match = price_type.options.some(({ value }) => price_type.value === value);
        !is_match && (price_type.value = price_type.options[0].value);
        price_type.on.change();
      },
    };

    // 出价方式
    price_type.on = {
      change: () => {
        const is_ocpx = price_type.value === '5';
        price_strategy.hidden = !is_ocpx;
        price_strategy.on.change();
        fusion__optimize_goal.hidden = !is_ocpx;

        default_second_stage.hidden = !is_ocpx;
      },
    };

    // 出价策略
    price_strategy.on = {
      change: () => {
        const is_ocpx = price_type.value === '5';
        const is_max = price_strategy.value === '1';

        price.hidden = is_ocpx && is_max;

        daily_budget_type.props.disabled = is_ocpx && is_max;
        (is_ocpx && is_max) && (daily_budget_type.value = 'BUDGET_MODE_DAY');
        daily_budget_type.on.change();

        default_second_stage.props.disabled = is_ocpx && is_max;
        (is_ocpx && is_max) && (default_second_stage.value = '1');

        fusion__optimize_goal.on.change();
      },
    };

    // 优化目标
    fusion__optimize_goal.on = {
      change: async() => {
        const is_has_optimize_goal = !!fusion__optimize_goal.value;
        const is_ocpx = price_type.value === '5';
        const is_max = price_strategy.value === '1';
        ocpc_price.hidden = !(is_ocpx && !is_max && is_has_optimize_goal);

        if (fusion__optimize_goal.value !== fusion__optimize_goal.backup_value) {
          fusion__optimize_goal.backup_value = fusion__optimize_goal.value;

          // 获取深度优化目标
          deep_convert_type.options = await controller.getDeepConvertGoalList({
            oppo_params: {
              filtering: {
                ...(site_sets.value.length && { site_set_id: site_sets.value[0] }),
                delivery_platform_type: delivery_platform_type.value,
                convert_goal: fusion__optimize_goal.value,
              },
            },
          });
          const is_match_deep_convert_type = deep_convert_type.options.some(({ value }) => deep_convert_type.value === value);
          !is_match_deep_convert_type && (deep_convert_type.value = deep_convert_type.options.length ? deep_convert_type.options[0].value : '');
        }

        deep_convert_type.hidden = !(is_ocpx && is_has_optimize_goal && deep_convert_type.options.length);
        deep_convert_type.on.change();
      },
    };

    // 深度优化目标
    deep_convert_type.on = {
      change: async() => {
        const is_has_deep_convert_type = !!deep_convert_type.value && !deep_convert_type.hidden;
        const is_ocpx = price_type.value === '5';
        const is_max = price_strategy.value === '1';
        // 深度优化目标为游戏付费ROI1、游戏变现ROI1、游戏变现ROI7、变现ROI14、快应用变现ROI1时，显示深度ROI系数出价
        const roi_list = ['23', '41', '42', '46', '47'];
        const is_roi = roi_list.includes(deep_convert_type.value);
        deep_convert_price.hidden = !(is_ocpx && !is_max && is_has_deep_convert_type && !is_roi);
        target_roi.hidden = !(is_ocpx && !is_max && is_has_deep_convert_type && is_roi);
        const is_13 = site_sets.value[0] === '13';
        // 自动优化 - 广告版位为展示优选 && 深度优化目标有值
        ocpx_optimize_switch.hidden = !(is_13 && is_ocpx && is_has_deep_convert_type);
        ocpx_optimize_switch.on.change();
      },
    };

    // 自动优化
    ocpx_optimize_switch.on = {
      change: () => {
        const is_ocpx = price_type.value === '5';
        const is_open = ocpx_optimize_switch.value === '1';
        const is_hidden = ocpx_optimize_switch.hidden;
        ocpx_optimize_type.hidden = !(is_ocpx && is_open && !is_hidden);
      },
    };

    const {
      creative_config: {
        config: {
          content_form,
        },
      },
    } = config;
    const { creative_template_id } = content_form.config;

    // 创意形式
    creative_template_id.on = {
      change: async() => {
        // 获取创意动态组件
        if (creative_template_id.value !== creative_template_id.backup_value) {
          creative_template_id.backup_value = creative_template_id.value;
          const {
            campaign_config: {
              config: {
                delivery_form: {
                  config: {
                    delivery_platform_type,
                  },
                },
                site_form: {
                  config: {
                    site_sets,
                  },
                },
              },
            },
          } = config;

          const params = {
            filtering: {
              site_sets: site_sets.value,
            },
            oppo_params: {
              filtering: {
                delivery_platform_type: delivery_platform_type.value,
                creative_template_id: creative_template_id.value,
              },
            },
          };

          this.renderDynamicComponent({ config, params, controller });
        }
      },
    };

    const {
      targeting_config: {
        config: {
          targeting_form,
        },
      },
    } = config;
    const {
      ages: {
        children: {
          ages_selection,
          ages,
        },
      },
      network_types,
      device_prices,
      devices: {
        children: {
          device_selection,
          devices,
        },
      },
      app_install: {
        children: {
          app_install_selection,
          app_install,
          app_uninstall_days,
        },
      },
      promote_app_installed: {
        children: {
          promote_app_installed_selection,
          promote_app_not_installed,
        },
      },
      app_active: {
        children: {
          app_active_selection,
          app_active,
          app_active_days,
        },
      },
      interest_tags_v2: {
        children: {
          interest_tags_v2_selection,
          interest_tags_v2,
        },
      },
      instant_install_list: {
        children: {
          instant_install_list_selection,
          instant_install_list,
        },
      },
      pre_install_app_list: {
        children: {
          pre_install_app_list_selection,
          pre_install_app_list,
        },
      },
      union_app_id: {
        children: {
          union_app_id_selection,
          union_app_id,
        },
      },
      filter_converted_ad: {
        children: {
          filter_converted_ad,
          filter_converted_ad_days,
        },
      },
    } = targeting_form.config;

    // 年龄
    ages_selection.on = {
      change: () => {
        const is_custom = ages_selection.value === OPTIONS_TYPE.CUSTOM;
        ages.hidden = !is_custom;
      },
    };

    // 联网方式
    network_types.on = {
      change: () => {
        network_types.value = checkboxFilterNone(network_types.value, OPTIONS_TYPE.UNLIMITED);
      },
    };

    // 机型价格
    device_prices.on = {
      change: () => {
        device_prices.value = checkboxFilterNone(device_prices.value, OPTIONS_TYPE.UNLIMITED);
      },
    };

    // 机型
    device_selection.on = {
      change: () => {
        const is_custom = device_selection.value === OPTIONS_TYPE.CUSTOM;
        devices.hidden = !is_custom;
      },
    };

    // APP安装
    app_install_selection.on = {
      change: () => {
        const is_unlimited = app_install_selection.value === OPTIONS_TYPE.UNLIMITED;
        const is_never_install_app_list = app_install_selection.value === 'never_install_app_list';
        app_install.hidden = is_unlimited;
        app_uninstall_days.hidden = !is_never_install_app_list;
      },
    };

    // 推广应用安装
    promote_app_installed_selection.on = {
      change: () => {
        const is_promote_app_not_installed = promote_app_installed_selection.value === 'promote_app_not_installed';
        promote_app_not_installed.hidden = !is_promote_app_not_installed;
      },
    };

    // 用户活跃
    app_active_selection.on = {
      change: () => {
        const is_app_active = app_active_selection.value === 'app_active';
        const is_app_not_active = app_active_selection.value === 'app_not_active';
        app_active.hidden = !(is_app_active || is_app_not_active);
        app_active_days.hidden = !(is_app_active || is_app_not_active);
        app_active_days.label = is_app_active ? '用户活跃天数' : '用户非活跃天数';
        app_active_days.rules.message = is_app_active ? '请输入用户活跃天数' : '请输入用户非活跃天数';
      },
    };

    // 用户兴趣行为
    interest_tags_v2_selection.on = {
      change: () => {
        const is_custom = interest_tags_v2_selection.value === OPTIONS_TYPE.CUSTOM;
        interest_tags_v2.hidden = !is_custom;
      },
    };

    // 快应用桌面状态
    instant_install_list_selection.on = {
      change: () => {
        const is_unlimited = instant_install_list_selection.value === OPTIONS_TYPE.UNLIMITED;
        instant_install_list.hidden = is_unlimited;
      },
    };

    // 预装APP
    pre_install_app_list_selection.on = {
      change: () => {
        const is_unlimited = pre_install_app_list_selection.value === OPTIONS_TYPE.UNLIMITED;
        pre_install_app_list.hidden = is_unlimited;
      },
    };

    // 流量定向
    union_app_id_selection.on = {
      change: () => {
        const is_unlimited = union_app_id_selection.value === OPTIONS_TYPE.UNLIMITED;
        union_app_id.hidden = is_unlimited;
      },
    };

    // 屏蔽已转化用户
    filter_converted_ad.on = {
      change: () => {
        const is_app = filter_converted_ad.value === 6;
        filter_converted_ad_days.hidden = !is_app;
      },
    };
  }

  /**
   * 获取覆盖字符对象
   * @returns {Object}
   */
  getDifferentiatedOverrides() {
    // 品牌LOGO
    const brand_logo = generateFieldName([CREATIVE_MODULE_KEY, CREATIVE_COMPONENTS_FORM_KEY, 'brand_logo']);

    // 视频结束页背景图
    const video_background_image = generateFieldName([CREATIVE_MODULE_KEY, CREATIVE_COMPONENTS_FORM_KEY, 'video_background_image']);

    return {
      [brand_logo]: ({ field_config }) => {
        field_config.type = '';
        field_config.isPreview = true;
      },

      [video_background_image]: ({ field_config }) => {
        field_config.type = '';
        field_config.isPreview = true;
      },
    };
  }

  /**
   * 设置配置模块的数据源
   * @param {Object} config 模块表单配置
   * @param {Object} controller 控制器
   */
  async setFieldDataSources(config, controller) {
    try {
      await super.setFieldDataSources(config, controller);
      const {
        campaign_config: {
          config: {
            delivery_form,
            site_form,
            budget_and_bid_form,
          },
        },
      } = config;
      const { button_text } = creative_components_config;
      const { delivery_platform_type } = delivery_form.config;
      const { site_sets } = site_form.config;
      const { price_type, fusion__optimize_goal, ocpx_optimize_type } = budget_and_bid_form.config;
      price_type.options = price_type.options_map['13'];
      const promises_map = [
        // 优化目标
        [
          () => controller.getConvertGoalList({
            oppo_params: {
              filtering: {
                site_set_id: site_sets.value[0],
                delivery_platform_type: delivery_platform_type.value,
              },
            },
          }),
          (res) => {
            fusion__optimize_goal.options = res;
          },
        ],
        // 自动优化目标
        [
          () => controller.getOcpxOptimizeTypeEnums(),
          (res) => {
            ocpx_optimize_type.options = res;
          },
        ],
        // 按钮文案
        [
          () => controller.getButtonTitle(),
          (res) => {
            button_text.options = res;
          },
        ],
      ];
      const values = await Promise.all(promises_map.map(([promise]) => promise()));
      promises_map.forEach(([_, handler], index) => handler(values[index]));
    } catch (error) {
      console.error(error);
      throw new Error('OppoFieldStrategy: setFieldDataSources fail');
    }
  }

  /**
   * 设置定向模块的数据源
   * @param {Object} config 模块表单配置
   * @param {Object} controller 控制器
   */
  async setTargetingDataSources(config, controller) {
    await super.setTargetingDataSources(config, controller);
    const {
      audience: {
        children: {
          include_audiences,
          exclude_audiences,
        },
      },
      ages: {
        children: {
          ages,
        },
      },
      network_types,
      device_prices,
      devices: {
        children: {
          devices,
        },
      },
      app_install: {
        children: {
          app_install,
          app_uninstall_days,
        },
      },
      promote_app_installed: {
        children: {
          promote_app_not_installed,
        },
      },
      app_active: {
        children: {
          app_active,
        },
      },
      interest_tags_v2: {
        children: {
          interest_tags_v2,
        },
      },
      instant_install_list: {
        children: {
          instant_install_list,
        },
      },
      pre_install_app_list: {
        children: {
          pre_install_app_list,
        },
      },
      union_app_id: {
        children: {
          union_app_id,
        },
      },
      filter_converted_ad: {
        children: {
          filter_converted_ad_days,
        },
      },
    } = config.targeting_config.config.targeting_form.config;

    // 自定义人群包
    const audience_options = include_audiences.options.map(({ value, oppo_params, ...rest }) => {
      return {
        value: `${value}#${oppo_params.audience_file}`,
        ...rest,
      };
    });
    include_audiences.options = audience_options;
    exclude_audiences.options = audience_options;

    // 定向字典
    const targeting_dictionary = await controller.getTargetingDictionary(['age', 'device', 'device_price', 'network_type', 'interest_tag', 'app', 'union_app']);
    const unlimited_selection = { label: '不限', value: OPTIONS_TYPE.UNLIMITED };

    // 年龄
    ages.options = targeting_dictionary?.age || [];
    // 联网方式
    network_types.options = [unlimited_selection, ...(targeting_dictionary?.network_type || [])];
    // 设备价格
    device_prices.options = [unlimited_selection, ...(targeting_dictionary?.device_price || [])];
    // 机型
    devices.options = targeting_dictionary?.device || [];
    // APP安装
    app_install.options = targeting_dictionary?.app || [];
    app_uninstall_days.options = await controller.getAppUninstallDaysEnums();
    // 推广应用安装
    promote_app_not_installed.options = await controller.getPromoteAppNotInstalledEnums();
    // 用户活跃
    app_active.options = targeting_dictionary?.app || [];
    // 用户兴趣行为
    interest_tags_v2.options = targeting_dictionary?.interest_tag || [];
    // 快应用桌面状态
    instant_install_list.options = targeting_dictionary?.app || [];
    // 预装APP
    pre_install_app_list.options = targeting_dictionary?.app || [];
    // 流量定向
    union_app_id.options = targeting_dictionary?.union_app || [];
    // 屏蔽已转化用户
    filter_converted_ad_days.options = await controller.getFilterConvertedAdDaysEnums();
  }

  /**
   * 模块字段排序
   * @param {Object} fields
   * @returns {Object}
   */
  async setFieldsSort(fields) {
    const { campaign_config } = fields;
    const {
      daily_budget_type,
      daily_budget,
      price_type,
      price_strategy,
      convert_id,
      fusion__optimize_goal,
      deep_convert_type,
      price,
      default_second_stage,
      ocpc_price,
      deep_convert_price,
      target_roi,
      ocpx_optimize_switch,
      ocpx_optimize_type,
      ...rest
    } = campaign_config.config.budget_and_bid_form.config;
    campaign_config.config.budget_and_bid_form.config = {
      daily_budget_type,
      daily_budget,
      price_type,
      price_strategy,
      convert_id,
      fusion__optimize_goal,
      deep_convert_type,
      price,
      default_second_stage,
      ocpc_price,
      deep_convert_price,
      target_roi,
      ocpx_optimize_switch,
      ocpx_optimize_type,
      ...rest,
    };
    return Promise.resolve(fields);
  }

  /**
   * 设置动态渲染组件
   * @param {Object} config 模块表单配置
   * @param {Object} controller 控制器
   * @param {Object} payload 传递的数据，不限制键名和值
   */
  async setDynamicComponent(config, controller, payload) {
    if (config && controller && payload) {
      const { raw_ref_form_model, raw_form_model, form_assign_data } = payload;
      const is_site_set_type_changed = JSON.stringify(raw_ref_form_model['campaign_config.site_form']?.site_set_type) !== JSON.stringify(raw_form_model['campaign_config.site_form']?.site_set_type);
      const is_site_sets_changed = JSON.stringify(raw_ref_form_model['campaign_config.site_form']?.site_sets) !== JSON.stringify(raw_form_model['campaign_config.site_form']?.site_sets);
      const is_delivery_platform_type_changed = JSON.stringify(raw_ref_form_model['campaign_config.delivery_form']?.delivery_platform_type) !== JSON.stringify(raw_form_model['campaign_config.delivery_form']?.delivery_platform_type);

      if (is_site_set_type_changed || is_site_sets_changed || is_delivery_platform_type_changed) {
        delete form_assign_data.creative_config;

        const {
          creative_config: {
            config: {
              content_form: {
                config: {
                  creative_template_id,
                },
              },
            },
          },
        } = config;

        const template_params = {
          filtering: {
            site_sets: raw_ref_form_model['campaign_config.site_form']?.site_sets,
            delivery_platform_type: raw_ref_form_model['campaign_config.delivery_form']?.delivery_platform_type,
          },
        };
        // 获取创意规格选项
        creative_template_id.options = await controller.getCreativeTemplateList(template_params);
        creative_template_id.value = creative_template_id.options[0].value;
        creative_template_id.backup_value = creative_template_id.value;

        const params = {
          filtering: {
            site_sets: raw_ref_form_model['campaign_config.site_form']?.site_sets,
          },
          oppo_params: {
            filtering: {
              delivery_platform_type: raw_ref_form_model['campaign_config.delivery_form']?.delivery_platform_type,
              creative_template_id: creative_template_id.value,
            },
          },
        };

        await this.renderDynamicComponent({ config, params, controller });
      }
    }
  }

  /**
   * 渲染创意动态组件
   * @param {Object} data 配置项
   * @param {Object} data.config 模块表单配置
   * @param {Object} data.params 查询参数
   * @param {Object} data.controller 控制器
   * @param {string[]} data.creative_config_keys 创意配置键
   */
  async renderDynamicComponent({ config, params, controller }) {
    const {
      creative_config: {
        config: {
          creative_components,
        },
      },
      material_config: {
        config: {
          material_form,
        },
      },
    } = config;

    const { names, list } = await controller.getDynamicComponent(params);
    // 重新初始化【创意组件】表单配置
    creative_components.config = clone(creative_components_config);
    const dynamic_component_keys = ['brand_name', 'brand_logo', 'button_text', 'video_background_image'];

    [creative_components].forEach(target => {
      Object.keys(target.config).forEach(key => {
        if (!names.includes(key) && dynamic_component_keys.includes(key)) {
          delete target.config[key];
        }
      });
      const ret = list.filter(item => target.config[item.name]);
      list.forEach((item) => Object.assign(item, { element_type: target.config[item.name]?.type, default_value: target.config[item.name]?.default_value }));
      target.config = deepMerge([target.config, generateDynamicFormModel(ret)]);
    });
    this.addEvent(config, controller);

    const {
      material,
    } = material_form.config;

    const mapTuple = [
      // 图片素材限制
      [
        () => names.includes('image'),
        () => {
          const image = list.find(item => item.name === 'image');
          const width = image?.restriction?.image_restriction?.width;
          const height = image?.restriction?.image_restriction?.height;
          const is_has_restriction = width && height;
          const is_image_list = image?.array_property?.max_number > 1;
          material.materialRestriction = is_has_restriction ? [{ width, height }] : [];
          material.material_type = is_image_list ? 'element_story' : 'image';
          material.value.videos = [];
          material.value.tab = 'image';
        },
      ],
      // 视频素材限制
      [
        () => names.includes('video'),
        () => {
          const video = list.find(item => item.name === 'video');
          const width = video?.restriction?.video_restriction?.min_width;
          const height = video?.restriction?.video_restriction?.min_height;
          const is_has_restriction = width && height;
          material.materialRestriction = is_has_restriction ? [{ width, height }] : [];
          material.material_type = 'video';
          material.value.images = [];
          material.value.tab = 'video';
        },
      ],
      // 不限制素材
      [
        () => !names.includes('video') && !names.includes('image'),
        () => {
          material.materialRestriction = [];
          material.material_type = null;
          material.value.tab = 'video';
        },
      ],
    ];
    mapTuple.forEach(([condition, handler]) => (condition() && handler()));
  }

  /**
   * 转换差异化广告预览参数
   * @param {Object} data 数据
   * @param {Object} data.form_assign_data 提交参数
   */
  transformDifferentiatedCampaignPreviewPayload({ form_assign_data }) {
    const {
      campaign_config: {
        delivery_form: {
          delivery_platform_type,
          instant_app_url,
        },
        site_form: {
          site_sets,
          link_desk_flag,
        },
        schedule_form: {
          time_period_type,
          schedule_time,
          time_period,
        },
        budget_and_bid_form: {
          fusion__optimize_goal,
          deep_convert_type,
          default_second_stage,
          ocpx_optimize_switch,
          ocpx_optimize_type,
          ocpc_price,
          target_roi,
        },
      },
    } = form_assign_data;

    return {
      // 投放时间类型
      time_period_type,
      // 投放时间 - 全天
      ...(time_period_type === 'TIME_PERIOD_ALL' && { time_period: new Array(TIME_PERIOD_LENGTH.HALF_HOUR).fill(1).join('') }),
      // 投放时间 - 指定时间
      ...(time_period_type === 'TIME_PERIOD_DAY_SPECIFIC' && { time_period: transformSelectedTimeToTimeSeries(schedule_time.start, schedule_time.end) }),
      // 投放时间 - 指定多个时间
      ...(time_period_type === 'TIME_PERIOD_HOUR_SPECIFIC' && { time_period: time_period.schedule_time_content }),
      // 快应用直达链接
      ...(delivery_platform_type === '3' && { instant_app_url: `hap://game/${instant_app_url}/?_FORCE_UPDATE_=1` }),
      // 快应用加桌 - 广告版位为信息流时支持
      ...(site_sets[0] === '6' && { link_desk_flag }),
      // 优化目标
      ...(fusion__optimize_goal && { convert_goal: fusion__optimize_goal }),
      // 深度优化目标
      ...(deep_convert_type && { deep_convert_type }),
      // 广告免一阶
      ...(default_second_stage && { default_second_stage }),
      // 自动优化
      ...(ocpx_optimize_switch && { ocpx_optimize_switch }),
      // 自动优化目标
      ...(ocpx_optimize_type && { ocpx_optimize_type }),
      // 优化目标出价
      ...(ocpc_price && { ocpc_price: calculator.times(ocpc_price, 100) }),
      // 深度ROI系数出价
      ...(target_roi && { target_roi }),
    };
  }

  /**
   * 转换差异化创意预览参数
   * @param {Object} data 数据
   * @param {Object} data.form_assign_data 提交参数
   * @returns {Object}
   */
  transformDifferentiatedCreativePreviewPayload({ form_assign_data }) {
    const {
      creative_config: {
        content_form: {
          creative_template_id,
        } = {},
        creative_components: {
          brand_name,
          brand_logo,
          button_text,
          video_background_image,
        } = {},
      },
    } = form_assign_data;

    return {
      // 创意规格ID
      creative_template_id,
      // 品牌名称
      ...(brand_name && { brand_name }),
      // 品牌logo图片ID
      ...(brand_logo && { brand_logo_id: brand_logo.id }),
      // 按钮文案
      ...(button_text && { button_text }),
      // 视频结束页背景图ID
      ...(video_background_image && { video_background_image_id: video_background_image.id }),
    };
  }

  /**
   * 获取创建定向包差异化接口参数
   * @param {Object} target_data 定向数据
   * @returns {Object}
   */
  getDifferentiatedCreateTargetParams(target_data) {
    const {
      audience: {
        audience_types,
        include_audiences,
        exclude_audiences,
      },
      ages: {
        ages_selection,
        ages,
      },
      network_types,
      device_prices,
      devices: {
        device_selection,
        devices,
      },
      app_install: {
        app_install_selection,
        app_install,
        app_uninstall_days,
      },
      promote_app_installed: {
        promote_app_installed_selection,
        promote_app_not_installed,
      },
      app_active: {
        app_active_selection,
        app_active,
        app_active_days,
      },
      interest_tags_v2: {
        interest_tags_v2_selection,
        interest_tags_v2,
      },
      instant_install_list: {
        instant_install_list_selection,
        instant_install_list,
      },
      pre_install_app_list: {
        pre_install_app_list_selection,
        pre_install_app_list,
      },
      union_app_id: {
        union_app_id_selection,
        union_app_id,
      },
      filter_converted_ad: {
        filter_converted_ad,
        filter_converted_ad_days,
      },
    } = target_data;

    const target_params = {
      [`${CHANNEL_INTERFACE_TYPE.OPPO}_params`]: {
        // 年龄
        ...(ages_selection === OPTIONS_TYPE.CUSTOM && { ages }),
        // 网络
        ...(!network_types.includes(OPTIONS_TYPE.UNLIMITED) && { network_types }),
        // 机型价格
        ...(!device_prices.includes(OPTIONS_TYPE.UNLIMITED) && { device_prices }),
        // 机型
        ...(device_selection === OPTIONS_TYPE.CUSTOM && { devices }),
        // APP安装
        ...(app_install_selection !== OPTIONS_TYPE.UNLIMITED && {
          [app_install_selection]: app_install,
          ...(app_install_selection === 'never_install_app_list' && { app_uninstall_days: +app_uninstall_days }),
        }),
        // 推广应用安装
        ...(promote_app_installed_selection !== OPTIONS_TYPE.UNLIMITED && {
          ...(promote_app_installed_selection === 'promote_app_installed' && { promote_app_installed: 1 }),
          ...(promote_app_installed_selection === 'promote_app_not_installed' && { promote_app_not_installed: +promote_app_not_installed }),
        }),
        // 用户活跃
        ...(app_active_selection !== OPTIONS_TYPE.UNLIMITED && {
          [app_active_selection]: app_active,
          [`${app_active_selection}_days`]: app_active_days,
        }),
        // 用户兴趣行为
        ...(interest_tags_v2_selection === OPTIONS_TYPE.CUSTOM && { interest_tags_v2 }),
        // 快应用桌面状态
        ...(instant_install_list_selection !== OPTIONS_TYPE.UNLIMITED && { [instant_install_list_selection]: instant_install_list }),
        // 预装APP
        ...(pre_install_app_list_selection !== OPTIONS_TYPE.UNLIMITED && { [pre_install_app_list_selection]: pre_install_app_list }),
        // 流量定向
        ...(union_app_id_selection !== OPTIONS_TYPE.UNLIMITED && { [union_app_id_selection]: union_app_id }),
        // 屏蔽已转化用户
        ...(filter_converted_ad !== OPTIONS_TYPE.UNLIMITED && {
          filter_converted_ad,
          ...(filter_converted_ad === 6 && { filter_converted_ad_days: +filter_converted_ad_days }),
        }),
        // 人群文件
        ...((audience_types.includes('INCLUDE') && include_audiences?.length) && { include_audience_file: include_audiences[0].split('#')[1] }),
        ...((audience_types.includes('EXCLUDE') && exclude_audiences?.length) && { exclude_audience_file: exclude_audiences[0].split('#')[1] }),
      },
      audience: {
        audience_types,
        ...((audience_types.includes('INCLUDE') && include_audiences?.length) && { include_audiences: [include_audiences[0].split('#')[0]] }),
        ...((audience_types.includes('EXCLUDE') && exclude_audiences?.length) && { exclude_audiences: [exclude_audiences[0].split('#')[0]] }),
      },
    };

    return target_params;
  }

  /**
   * 获取定向包差异化详情表单数据
   * @param {Object} target_params 定向详情数据
   * @returns {Object}
   */
  getDifferentiatedTargetDetailFormData(target_params) {
    const {
      audience: {
        audience_types,
        include_audiences,
        exclude_audiences,
      },
      [`${CHANNEL_INTERFACE_TYPE.OPPO}_params`]: {
        ages,
        network_types,
        device_prices,
        devices,
        install_app_list,
        not_install_app_list,
        never_install_app_list,
        yesterday_installed_app,
        installed_never_active_app_list,
        app_uninstall_days,
        promote_app_installed,
        promote_app_not_installed,
        app_active,
        app_not_active,
        app_active_days,
        app_not_active_days,
        interest_tags_v2,
        instant_install_list,
        instant_not_install_list,
        pre_install_app_list,
        un_pre_install_app_list,
        union_app_id,
        union_app_id_re,
        filter_converted_ad,
        filter_converted_ad_days,
        include_audience_file,
        exclude_audience_file,
      } = {},
    } = target_params;

    const app_install_params = Object.entries({ install_app_list, not_install_app_list, never_install_app_list, yesterday_installed_app, installed_never_active_app_list }).filter(([_, value]) => isNotNullAndUnDef(value))[0];
    const promote_app_installed_params = Object.entries({ promote_app_installed, promote_app_not_installed }).filter(([_, value]) => isNotNullAndUnDef(value))[0];
    const app_active_params = Object.entries({ app_active, app_not_active }).filter(([_, value]) => isNotNullAndUnDef(value))[0];
    const app_active_days_params = Object.entries({ app_active_days, app_not_active_days }).filter(([_, value]) => isNotNullAndUnDef(value))[0];
    const instant_install_list_params = Object.entries({ instant_install_list, instant_not_install_list }).filter(([_, value]) => isNotNullAndUnDef(value))[0];
    const pre_install_app_list_params = Object.entries({ pre_install_app_list, un_pre_install_app_list }).filter(([_, value]) => isNotNullAndUnDef(value))[0];
    const union_app_id_params = Object.entries({ union_app_id, union_app_id_re }).filter(([_, value]) => isNotNullAndUnDef(value))[0];

    const form_data = {
      // 自定义人群
      audience: {
        audience_types,
        ...(include_audiences?.length && { include_audiences: [`${include_audiences[0]}#${include_audience_file}`] }),
        ...(exclude_audiences?.length && { exclude_audiences: [`${exclude_audiences[0]}#${exclude_audience_file}`] }),
      },
      // 年龄
      ...(ages && {
        ages: {
          ages_selection: OPTIONS_TYPE.CUSTOM,
          ages,
        },
      }),
      // 网络
      ...(network_types && { network_types }),
      // 机型价格
      ...(device_prices && { device_prices }),
      // 机型
      ...(devices && {
        devices: {
          device_selection: OPTIONS_TYPE.CUSTOM,
          devices,
        },
      }),
      // APP安装
      ...(app_install_params?.length && {
        app_install: {
          app_install_selection: app_install_params[0],
          app_install: app_install_params[1],
          ...(app_install_params[0] === 'never_install_app_list' && { app_uninstall_days: String(app_uninstall_days) }),
        },
      }),
      // 推广应用安装
      ...(promote_app_installed_params?.length && {
        promote_app_installed: {
          promote_app_installed_selection: promote_app_installed_params[0],
          ...(promote_app_installed_params[0] === 'promote_app_not_installed' && { promote_app_not_installed: String(promote_app_not_installed) }),
        },
      }),
      // 用户活跃
      ...(app_active_params?.length && {
        app_active: {
          app_active_selection: app_active_params[0],
          app_active: app_active_params[1],
          app_active_days: app_active_days_params[1],
        },
      }),
      // 用户兴趣行为
      ...(interest_tags_v2 && {
        interest_tags_v2: {
          interest_tags_v2_selection: OPTIONS_TYPE.CUSTOM,
          interest_tags_v2,
        },
      }),
      // 快应用桌面状态
      ...(instant_install_list_params?.length && {
        instant_install_list: {
          instant_install_list_selection: instant_install_list_params[0],
          instant_install_list: instant_install_list_params[1],
        },
      }),
      // 预装APP
      ...(pre_install_app_list_params?.length && {
        pre_install_app_list: {
          pre_install_app_list_selection: pre_install_app_list_params[0],
          pre_install_app_list: pre_install_app_list_params[1],
        },
      }),
      // 流量定向
      ...(union_app_id_params?.length && {
        union_app_id: {
          union_app_id_selection: union_app_id_params[0],
          union_app_id: union_app_id_params[1],
        },
      }),
      // 屏蔽已转化用户
      ...(filter_converted_ad && {
        filter_converted_ad: {
          filter_converted_ad,
          ...(filter_converted_ad === 6 && { filter_converted_ad_days: String(filter_converted_ad_days) }),
        },
      }),
    };

    return form_data;
  }

  /**
   * 获取定向数据转预览数据关联表
   * @returns {Object}
   */
  getTargetPreviewAssociatedMap() {
    return {
      ages_selection: {
        associated_keys: ['ages'],
      },
      device_selection: {
        associated_keys: ['devices'],
      },
      interest_tags_v2_selection: {
        associated_keys: ['interest_tags_v2'],
      },
    };
  }

  /**
   * 获取素材属性
   * @param {Object} material_config 素材表单配置
   * @returns {Object}
   */
  getMaterialProps(material_config) {
    const { materialRestriction, material_type } = material_config?.config?.material_form?.config?.material;
    return {
      materialRestriction,
      ...(material_type && { type: material_type }),
    };
  }

  /**
   * 设置源数据表单
   * @param {Object} source 源数据
   * @param {Object} target 目标数据
   * @returns {void}
   */
  setSourceFormFieldsData(source, target) {
    const {
      material_config: {
        config: {
          material_form: {
            config: {
              material,
            },
          },
        },
      },
    } = source;

    const {
      material_config: {
        config: {
          material_form: {
            config: {
              material: target_material,
            },
          },
        },
      },
    } = target;

    // 保持数据引用
    material.value.images.splice(0);
    material.value.images.push(...target_material.value.images);
    material.value.videos.splice(0);
    material.value.videos.push(...target_material.value.videos);
    material.value.tab = target_material.value.tab;

    Object.assign(material, {
      materialRestriction: target_material.materialRestriction,
      material_type: target_material.material_type,
    });
  }

  /**
   * 获取差异化模板配置表
   * @returns {Object}
   */
  getDifferentiatedTemplateMap() {
    return {
      campaign_config: {
        budget_and_bid_form: ['default_second_stage'],
      },
      creative_config: {
        content_form: ['delivery_mode', 'creative_template_id'],
      },
    };
  }

  /**
   * 是否重新渲染动态创意
   * @param {Object} old_campaign_config 旧广告配置
   * @param {Object} new_campaign_config 新广告配置
   * @returns {Boolean}
   */
  isReRenderCreative(old_campaign_config, new_campaign_config) {
    const {
      delivery_form: {
        config: {
          delivery_platform_type: old_delivery_platform_type,
        },
      },
      site_form: {
        config: {
          site_set_type: old_site_set_type,
          site_sets: old_site_sets,
        },
      },
    } = old_campaign_config.config;

    const {
      delivery_form: {
        config: {
          delivery_platform_type: new_delivery_platform_type,
        },
      },
      site_form: {
        config: {
          site_set_type: new_site_set_type,
          site_sets: new_site_sets,
        },
      },
    } = new_campaign_config.config;

    const is_delivery_platform_type_changed = JSON.stringify(old_delivery_platform_type.value) !== JSON.stringify(new_delivery_platform_type.value);
    const is_site_set_type_changed = JSON.stringify(old_site_set_type.value) !== JSON.stringify(new_site_set_type.value);
    const is_site_sets_changed = JSON.stringify(old_site_sets.value) !== JSON.stringify(new_site_sets.value);

    return is_delivery_platform_type_changed || is_site_set_type_changed || is_site_sets_changed;
  }

  /**
   * 是否显示预览按钮
   * @param {Object} form_assign_data 分配数据
   * @param {Object} form_fields 源数据
   * @param {Object} payload 配置参数
   * @returns {boolean}
   */
  isShowPreviewBtn(form_assign_data, form_fields) {
    const all_keys = [CAMPAIGN_MODULE_KEY, CREATIVE_MODULE_KEY, TARGETING_MODULE_KEY];
    const is_has_all_kays = all_keys.every(key => !!form_assign_data[key]);
    if (!is_has_all_kays) {
      return false;
    }

    const {
      campaign_config: {
        site_form: {
          site_sets,
        },
      },
      creative_config: {
        content_form: {
          creative_template_id,
        },
      },
    } = form_assign_data;
    // 不支持素材 - (展示优选 || 联盟通投 || 插屏 || 开屏 || 原生) && 图标大图
    const is_not_support_material = ['13', '12', '9', '8', '10'].includes(site_sets[0]) && [71].includes(creative_template_id);
    // 不支持标题 - 展示优选 && (图标大图 || 横版组图)
    const is_not_support_titles = ['13'].includes(site_sets[0]) && [71, 30].includes(creative_template_id);
    // 不支持落地页 - 展示优选
    const is_not_support_sites = ['13'].includes(site_sets[0]);

    if (is_not_support_material || is_not_support_titles || is_not_support_sites) {
      const material = form_assign_data[MATERIAL_MODULE_KEY];
      const is_has_material = is_not_support_material ? true : (material?.images?.length > 0 || material?.videos?.length > 0);
      const basic_keys = [CAMPAIGN_MODULE_KEY, CREATIVE_MODULE_KEY];
      const assign_keys = [
        TARGETING_MODULE_KEY,
        ...(is_not_support_titles ? [] : [TITLE_MODULE_KEY]),
        ...(is_not_support_sites ? [] : [SITE_MODULE_KEY]),
      ];
      return basic_keys.every(key => !!form_assign_data[key]) && assign_keys.every(key => form_assign_data[key]?.length > 0) && is_has_material;
    } else {
      return super.isShowPreviewBtn(form_assign_data, form_fields);
    }
  }
}
